<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CrmAPI from 'dashboard/api/crm';
import {
  DATE_REACHED_EVENT,
  DATE_TRIGGER_TEMPLATES,
  OFFER_ACTION,
  templateKeyForCampaign,
} from 'dashboard/routes/dashboard/settings/automation/dateTrigger';
import { useDateTriggerSummary } from 'dashboard/routes/dashboard/settings/automation/useDateTriggerSummary';

// As campanhas de recompra viraram automações "Em uma data": esta aba guarda a opção da conta,
// leva aos modelos e lista as automações de oferta e as campanhas que não migraram.
const AUTOMATION_LIST_ROUTE = 'automation_list';

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { dateSummary, lastRunAlert, lastRunText } = useDateTriggerSummary();
const currentAccountId = useMapGetter('getCurrentAccountId');
const getAccount = useMapGetter('accounts/getAccount');
const automations = useMapGetter('automations/getAutomations');

const deleteDialog = ref(null);
const campaignToDelete = ref(null);

// Opção da conta: quem já comprou pode receber oferta (com saída por SAIR).
const buyersOptIn = ref(false);
const syncBuyersOptIn = () => {
  buyersOptIn.value =
    getAccount.value(currentAccountId.value)?.settings?.crm_buyers_opt_in ===
    true;
};
const toggleBuyersOptIn = async () => {
  const next = !buyersOptIn.value;
  try {
    await CrmAPI.updateSettings({ buyers_opt_in: next });
    buyersOptIn.value = next;
    useAlert(t('CRM_RECOMPRA.BUYERS_OPT_IN.SAVED'));
  } catch (error) {
    useAlert(t('CRM_RECOMPRA.BUYERS_OPT_IN.ERROR'));
  }
};

const automationRoute = query => ({
  name: AUTOMATION_LIST_ROUTE,
  params: { accountId: currentAccountId.value },
  query,
});

const templateRoute = templateKey =>
  automationRoute({ new: 'date', template: templateKey });

const offerAutomations = computed(() =>
  (automations.value || []).filter(
    automation =>
      automation.event_name === DATE_REACHED_EVENT &&
      automation.actions.some(action => action.action_name === OFFER_ACTION)
  )
);

const campaigns = ref([]);
const notMigratedCampaigns = computed(() =>
  campaigns.value.filter(campaign => !campaign.migrated_to_automation_rule_id)
);

const notMigratedReason = campaign =>
  campaign.inbox_id
    ? t('CRM_RECOMPRA.NOT_MIGRATED.REASONS.MODULE_DISABLED')
    : t('CRM_RECOMPRA.NOT_MIGRATED.REASONS.NO_INBOX');

const fetchCampaigns = async () => {
  try {
    const { data } = await CrmAPI.getCampaigns();
    campaigns.value = data;
  } catch (error) {
    useAlert(t('CRM_RECOMPRA.NOT_MIGRATED.LOAD_ERROR'));
  }
};

const deleteCampaign = async campaign => {
  campaignToDelete.value = campaign;
  const confirmed = await deleteDialog.value.showConfirmation();
  if (!confirmed) return;
  try {
    await CrmAPI.deleteCampaign(campaign.id);
    campaigns.value = campaigns.value.filter(item => item.id !== campaign.id);
    useAlert(t('CRM_RECOMPRA.NOT_MIGRATED.DELETED'));
  } catch (error) {
    useAlert(t('CRM_RECOMPRA.NOT_MIGRATED.DELETE_ERROR'));
  }
};

onMounted(() => {
  // A lista de automações (e a tela de configurações a que ela leva) é só de administrador.
  if (isAdmin.value) store.dispatch('automations/get');
  syncBuyersOptIn();
  fetchCampaigns();
});
</script>

<template>
  <div class="flex flex-col w-full max-w-4xl gap-6 mx-auto">
    <label
      v-if="isAdmin"
      data-test="buyers-opt-in"
      class="flex items-start gap-3 px-4 py-3 mb-0 border cursor-pointer border-n-container rounded-xl bg-n-solid-1"
    >
      <input
        type="checkbox"
        class="mt-0.5 accent-n-brand"
        :checked="buyersOptIn"
        @change="toggleBuyersOptIn"
      />
      <span class="flex flex-col gap-0.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('CRM_RECOMPRA.BUYERS_OPT_IN.LABEL') }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ $t('CRM_RECOMPRA.BUYERS_OPT_IN.HINT') }}
        </span>
      </span>
    </label>

    <p
      data-test="migrated-notice"
      class="flex items-start gap-2 px-4 py-3 mb-0 text-sm rounded-xl bg-n-blue-3 text-n-blue-12"
    >
      <Icon icon="i-lucide-info" class="mt-0.5 shrink-0" />
      {{ $t('CRM_RECOMPRA.MIGRATED.NOTICE') }}
    </p>

    <section v-if="isAdmin" data-test="templates" class="flex flex-col gap-3">
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ $t('CRM_RECOMPRA.TEMPLATES.TITLE') }}
      </h3>
      <div class="grid gap-3 sm:grid-cols-3">
        <router-link
          v-for="template in DATE_TRIGGER_TEMPLATES"
          :key="template.key"
          :data-test="`template-${template.key}`"
          :to="templateRoute(template.key)"
          class="flex flex-col gap-1 p-4 border rounded-xl border-n-container bg-n-solid-1 hover:border-n-slate-6"
        >
          <Icon :icon="template.icon" class="size-5 text-n-slate-11" />
          <span class="text-sm font-medium text-n-slate-12">
            {{
              $t(
                `AUTOMATION.DATE_TRIGGER.TEMPLATES.${template.key.toUpperCase()}.NAME`
              )
            }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              $t(
                `CRM_RECOMPRA.TEMPLATES.${template.key.toUpperCase()}.DESCRIPTION`
              )
            }}
          </span>
        </router-link>
      </div>
    </section>

    <section
      v-if="isAdmin"
      data-test="offer-automations"
      class="flex flex-col gap-3"
    >
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ $t('CRM_RECOMPRA.OFFER_AUTOMATIONS.TITLE') }}
      </h3>
      <p
        v-if="!offerAutomations.length"
        data-test="offer-automations-empty"
        class="mb-0 text-sm text-n-slate-11"
      >
        {{ $t('CRM_RECOMPRA.OFFER_AUTOMATIONS.EMPTY') }}
      </p>
      <ul v-else class="flex flex-col gap-2 p-0 m-0 list-none">
        <li
          v-for="automation in offerAutomations"
          :key="automation.id"
          data-test="offer-automation"
          class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border rounded-xl border-n-container bg-n-solid-1"
        >
          <div class="flex flex-col min-w-0 gap-0.5">
            <span class="flex items-center gap-2 text-sm text-n-slate-12">
              {{ automation.name }}
              <span
                class="text-xs px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11"
              >
                {{
                  automation.active
                    ? $t('CRM_RECOMPRA.OFFER_AUTOMATIONS.ACTIVE')
                    : $t('CRM_RECOMPRA.OFFER_AUTOMATIONS.INACTIVE')
                }}
              </span>
            </span>
            <span class="text-xs text-n-slate-11">
              {{ dateSummary(automation.date_trigger) }}
            </span>
            <span
              v-if="lastRunAlert(automation.date_trigger.last_run)"
              class="text-xs text-n-ruby-11"
            >
              {{ lastRunAlert(automation.date_trigger.last_run) }}
            </span>
            <span
              v-else-if="lastRunText(automation.date_trigger.last_run)"
              class="text-xs text-n-slate-10"
            >
              {{ lastRunText(automation.date_trigger.last_run) }}
            </span>
          </div>
          <div class="flex gap-2">
            <router-link
              data-test="offer-automation-edit"
              :to="automationRoute({ edit: automation.id })"
              class="text-sm text-n-blue-11"
            >
              {{ $t('CRM_RECOMPRA.OFFER_AUTOMATIONS.EDIT') }}
            </router-link>
            <router-link
              data-test="offer-automation-history"
              :to="automationRoute({ history: automation.id })"
              class="text-sm text-n-blue-11"
            >
              {{ $t('CRM_RECOMPRA.OFFER_AUTOMATIONS.HISTORY') }}
            </router-link>
          </div>
        </li>
      </ul>
    </section>

    <section
      v-if="notMigratedCampaigns.length"
      data-test="not-migrated"
      class="flex flex-col gap-3"
    >
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ $t('CRM_RECOMPRA.NOT_MIGRATED.TITLE') }}
      </h3>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ $t('CRM_RECOMPRA.NOT_MIGRATED.HINT') }}
      </p>
      <ul class="flex flex-col gap-2 p-0 m-0 list-none">
        <li
          v-for="campaign in notMigratedCampaigns"
          :key="campaign.id"
          data-test="not-migrated-campaign"
          class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border rounded-xl border-n-container bg-n-solid-1"
        >
          <div class="flex flex-col gap-0.5">
            <span class="text-sm text-n-slate-12">{{ campaign.name }}</span>
            <span class="text-xs text-n-amber-11">
              {{ notMigratedReason(campaign) }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <router-link
              data-test="not-migrated-create"
              :to="templateRoute(templateKeyForCampaign(campaign))"
              class="text-sm text-n-blue-11"
            >
              {{ $t('CRM_RECOMPRA.NOT_MIGRATED.CREATE') }}
            </router-link>
            <NextButton
              v-if="isAdmin"
              data-test="not-migrated-delete"
              icon="i-lucide-trash"
              ruby
              ghost
              xs
              :label="$t('CRM_RECOMPRA.NOT_MIGRATED.DELETE')"
              @click="deleteCampaign(campaign)"
            />
          </div>
        </li>
      </ul>
    </section>

    <woot-confirm-modal
      ref="deleteDialog"
      :title="$t('CRM_RECOMPRA.NOT_MIGRATED.DELETE_CONFIRM.TITLE')"
      :description="
        $t('CRM_RECOMPRA.NOT_MIGRATED.DELETE_CONFIRM.DESCRIPTION', {
          name: campaignToDelete?.name,
        })
      "
      :confirm-label="$t('CRM_RECOMPRA.NOT_MIGRATED.DELETE_CONFIRM.YES')"
      :cancel-label="$t('CRM_RECOMPRA.NOT_MIGRATED.DELETE_CONFIRM.NO')"
    />
  </div>
</template>
